import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import Card from 'antd/es/card';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import Typography from 'antd/es/typography';
import React from 'react';
import styled from 'styled-components';

import type { JSONSchemaType } from '@mai/types';

const { Text } = Typography;
const { TextArea } = Input;

const StyledCard = styled(Card)`
  ${({ theme }) => `
  .ant-card-body {
    padding: ${theme.paddingLG}px;
  }
`}
`;

const HelpText = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextDescription};
  display: block;
  margin-top: ${theme.marginXXS}px;
`}
`;

interface SchemaBasicsProps {
  schema: JSONSchemaType;
  onUpdate: (updates: Partial<JSONSchemaType>) => void;
}

export const SchemaBasics: React.FC<SchemaBasicsProps> = ({
  schema,
  onUpdate,
}) => {
  return (
    <StyledCard
      title={
        <>
          <InfoCircleOutlined /> Schema Basics
        </>
      }
      size="small"
    >
      <Form layout="vertical" size="small">
        <Form.Item
          label="Title"
          extra={<HelpText>A descriptive title for your schema</HelpText>}
        >
          <Input
            value={schema.title || ''}
            onChange={(e) => onUpdate({ title: e.target.value })}
            placeholder="My Schema"
          />
        </Form.Item>

        <Form.Item
          label="Description"
          extra={<HelpText>Explain what this schema represents</HelpText>}
        >
          <TextArea
            rows={3}
            value={schema.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="This schema describes..."
          />
        </Form.Item>

        <Form.Item
          label="Root Type"
          extra={<HelpText>The type of the root object</HelpText>}
        >
          <Select
            value={schema.type || 'object'}
            onChange={(value) => onUpdate({ type: value })}
            style={{ width: '100%' }}
          >
            <Select.Option value="object">Object</Select.Option>
            <Select.Option value="array">Array</Select.Option>
            <Select.Option value="string">String</Select.Option>
            <Select.Option value="number">Number</Select.Option>
            <Select.Option value="boolean">Boolean</Select.Option>
          </Select>
        </Form.Item>

        {schema.type === 'object' && (
          <Form.Item
            label="Additional Properties"
            extra={
              <HelpText>Allow properties not defined in the schema</HelpText>
            }
          >
            <Select
              value={schema.additionalProperties === false ? 'false' : 'true'}
              onChange={(value) =>
                onUpdate({ additionalProperties: value === 'true' })
              }
              style={{ width: '100%' }}
            >
              <Select.Option value="true">Allow</Select.Option>
              <Select.Option value="false">Disallow</Select.Option>
            </Select>
          </Form.Item>
        )}
      </Form>
    </StyledCard>
  );
};
